import { Component, EventEmitter, Input, Output } from '@angular/core';
import {
  DecimalPipe,
  DatePipe,
  NgClass,
  AsyncPipe,
  NgIf,
} from '@angular/common';
import { RequestToBidButtonComponent } from '../../../../buyer-shared/components/request-to-bid-button/request-to-bid-button.component';
import { LocalDateTimePipe } from '../../../../../../Shared/_pipes/local-date-time';
import { NewlineToBrPipe } from '../../../../../../Shared/_pipes/newline-to-br.pipe';
import { TranslationsConvertKeyPipe } from '../../../../../../Shared/_pipes/translations-convert-key.pipe';
import { MakeOfferDialogComponent } from '../../../../buyer-shared/components/make-offer-dialog/make-offer-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MakeOfferBtnComponent } from '../../../../buyer-shared/components/make-offer-btn/make-offer-btn.component';
import { CurrencySymbolPipe } from 'src/app/Shared/_pipes/currency-symbol/currency-symbol.pipe';
import { HorseStatusDisplayService } from '../../../../buyer-shared/services/horse-status-display.service';
import { CustomCurrencyPipe } from 'src/app/Shared/_pipes/currency-pipe/custom-currency-pipe.pipe';
import { ToastService } from 'src/app/Shared/services/toast/toast.service';
import { AuctionService } from '../../_services/auction/auction.service';
import { BuyerService } from 'src/app/app-modules/buyer/_services/buyer/buyer.service';
import { LocaleService } from 'src/app/Shared/services/locale/locale.service';
import { environment } from 'src/environments/environment';
import { AuctionItemStatusComponent } from '../../../../buyer-shared/components/auction-item-status/auction-item-status.component';

@Component({
  selector: 'app-horse-top-details',
  standalone: true,
  templateUrl: './horse-top-details-new.component.html',
  styleUrls: ['./horse-top-details.component.scss'],
  imports: [
    NgIf,
    RequestToBidButtonComponent,
    MakeOfferBtnComponent,
    CustomCurrencyPipe,
    TranslatePipe,
    AuctionItemStatusComponent,
  ],
  providers: [CustomCurrencyPipe, CurrencySymbolPipe],
})
export class HorseTopDetailsComponent {
  @Input() fields: any = {};
  @Input() schema: any[] = [];
  @Input() aucType!: string;
  @Input() dir: any;
  @Input() userId: any;
  @Input() horseDetails: any;
  @Input() loggedIn!: boolean;
  @Input() canBid!: boolean;
  @Input() translations!: boolean;
  @Output() requestToBidClicked: EventEmitter<any> = new EventEmitter<any>();
  @Input() auctionSettings!: any;
  @Input() terms!: any;
  @Input() horseOffers!: any;
  @Input() locale!: string;
  @Input() currency!: any;
  @Input() buyerStatus: any;
  @Input() sellerEmail: string = '';
  @Output() loadData: EventEmitter<any> = new EventEmitter<any>();
  favClicked = false;
  isDev: boolean = environment.isDev;

  constructor(
    private dialog: MatDialog,
    private toastService: ToastService,
    private auctionService: AuctionService,
    private buyerService: BuyerService,
    private translate: TranslateService,
    public localeService: LocaleService,
    public statusService: HorseStatusDisplayService,
  ) {}

  handleLoadData() {
    this.loadData.emit();
  }

  // ==================== STATUS CHECKS (Delegated to Service) ====================

  get isUnSoldStatus(): boolean {
    return this.statusService.isNotSold(this.horseDetails);
  }

  get isRnaStatus(): boolean {
    return this.statusService.isRNA(this.horseDetails);
  }

  get isWinnerStatus(): boolean {
    return this.statusService.isWinner(this.horseDetails);
  }

  get isSoldStatus(): boolean {
    return this.statusService.isSold(this.horseDetails);
  }

  get isPSStatus(): boolean {
    return this.statusService.isPS(this.horseDetails);
  }

  get isWithdrawnStatus(): boolean {
    return this.statusService.isWithdrawn(this.horseDetails);
  }

  get isNoStatusStatus(): boolean {
    return this.statusService.isNoStatus(this.horseDetails);
  }

  /** No Status variant that hides the price row entirely (no "----"). */
  get isNoStatusHidePriceStatus(): boolean {
    return this.statusService.isNoStatusHidePrice(this.horseDetails);
  }

  /** No Status (sale) variant: no badge, shows Latest Bid + Highest Bidder (legacy display). */
  get isNoStatusSaleStatus(): boolean {
    return this.statusService.isNoStatusSale(this.horseDetails);
  }

  // ==================== DISPLAY DATA (From Backend) ====================

  get statusLabel(): string | null {
    return this.statusService.getStatusLabel(this.horseDetails);
  }

  get statusClass(): string {
    return this.statusService.getStatusClass(this.horseDetails);
  }

  get shouldShowStatus(): boolean {
    return this.statusService.shouldShowStatus(this.horseDetails);
  }

  get displayPrice(): string | null {
    return this.statusService.getFormattedPrice(this.horseDetails);
  }

  get priceLabel(): string | null {
    return this.statusService.getPriceLabel(this.horseDetails);
  }

  get buyerName(): string | null {
    return this.statusService.getBuyerName(this.horseDetails);
  }

  get shouldShowBuyerName(): boolean {
    return this.statusService.shouldShowBuyerName(this.horseDetails);
  }

  /**
   * Check if the horse has individually closed (has a status)
   * Used to show closed horse info even when auction is still active
   */
  get isHorseClosed(): boolean {
    return this.statusService.shouldShowStatus(this.horseDetails);
  }

  // ====================

  addToWishList() {
    this.toastService.clear();

    if (this.loggedIn) {
      this.favClicked = true;
      this.auctionService.addToWishlist(this.horseDetails?.id).subscribe({
        next: () => {
          this.horseDetails.in_user_wishlist = true;
          this.toastService.showSuccess(
            this.translate.instant('messages.success.added_to_wishlist'),
          );
          this.favClicked = false;
        },
        error: (error) => {
          this.favClicked = false;

          this.toastService.showError(error?.error?.Error[0]);
        },
      });
    } else {
      this.buyerService.setShowLogin(true);
    }
  }

  removeFromWishList() {
    this.toastService.clear();

    if (this.loggedIn) {
      this.favClicked = true;

      this.auctionService.deleteFromWishlist(this?.horseDetails?.id).subscribe({
        next: () => {
          this.horseDetails.in_user_wishlist = false;
          this.toastService.showSuccess(
            this.translate.instant('messages.success.removed_from_wishlist'),
          );

          this.favClicked = false;
        },
        error: (error) => {
          this.favClicked = false;

          this.toastService.showError(error?.error?.Error[0]);
        },
      });
    } else {
      this.buyerService.setShowLogin(true);
    }
  }
  ngOnInit(): void {}
  // handleRequestToBid(){
  //   this.requestToBidClicked.emit(true)

  // }
}
